/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Autocomplete,
  Box,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import {
  ChipSelectList,
  ChipOption,
  DebouncedSearchInput,
} from '@marageti/z4-lib';
import type { TripSearchDateRangeKey } from '../../../components/trip-search-date-range-filters/trip-search-date-range-filters';
import TripSearchDateRangeFilters from '../../../components/trip-search-date-range-filters/trip-search-date-range-filters';
import { Option, AssigneeOption } from '../options';

type AllFiltersProps = {
  onSearchChange: (v: string) => void;
  searchValue: string;
  orderBy: string;
  orderByOptions: Option[];
  sortBy: string;
  sortByOptions: Option[];
  onOrderSortChange: (n: string, v: string) => void;
  isLoadingDestinations: boolean;
  selectedDestinations: Option[];
  destinationOptions: Option[];
  onDestinationChange: (countries: Option[]) => void;
  assignees: AssigneeOption[];
  selectedAssignee: AssigneeOption;
  onAssigneeChange: (assignee: AssigneeOption | null) => void;
  onTagChange: (selectedTag: string) => void;
  tagOptions: ChipOption[];
  selectedTag: string;
  statusChips: ChipOption[];
  selectedStatusChips: string[];
  onStatusChipChange: (selectedChips: string[]) => void;
  departureDateFrom: string;
  departureDateTo: string;
  matchDateFrom: string;
  matchDateTo: string;
  onDateFilterChange: (name: TripSearchDateRangeKey, value: string) => void;
};

const fiveColumnRowSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    mobile: '1fr',
    desktop: 'repeat(5, minmax(0, 1fr))',
  },
};

const Cell = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>{children}</Box>
);

const AllFilters = ({
  // Search Props
  searchValue,
  onSearchChange,
  // Destination Props
  isLoadingDestinations,
  selectedDestinations,
  destinationOptions,
  onDestinationChange,
  // Assignee Props
  assignees,
  selectedAssignee,
  onAssigneeChange,
  // Sort and Order Props
  orderBy,
  orderByOptions,
  sortBy,
  sortByOptions,
  onOrderSortChange,
  // Chip Props
  onTagChange,
  tagOptions,
  selectedTag,
  statusChips,
  selectedStatusChips,
  onStatusChipChange,
  departureDateFrom,
  departureDateTo,
  matchDateFrom,
  matchDateTo,
  onDateFilterChange,
}: AllFiltersProps) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ ...fiveColumnRowSx, mb: 0, gap: { mobile: 0, desktop: 2 } }}>
      <Cell>
        <DebouncedSearchInput
          fullWidth
          key="fullText"
          label="Search"
          name="fullText"
          onChange={onSearchChange}
          defaultValue={searchValue}
        />
      </Cell>
      <Cell>
        <Autocomplete
          ChipProps={{
            size: 'small',
            sx: {
              backgroundColor: 'secondary.light',
            },
          }}
          fullWidth
          multiple
          getOptionLabel={(o) => o.title}
          id="primaryDestination"
          isOptionEqualToValue={(o, v) => o.value === v.value}
          loading={isLoadingDestinations}
          onChange={(e, v) => onDestinationChange(v)}
          options={destinationOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destinations"
              name="primaryDestination"
            />
          )}
          value={selectedDestinations}
        />
      </Cell>
      <Cell>
        <TextField
          fullWidth
          disabled={searchValue !== ''}
          key="tags-select"
          label="Tags"
          name="tag"
          onChange={(e) => onTagChange(e.target.value)}
          select
          value={selectedTag}
        >
          {tagOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Cell>
      <Cell>
        <Autocomplete
          getOptionKey={(o) => o.value}
          getOptionLabel={(o) => o.title}
          groupBy={(o) => o.status || ''}
          id="assignee"
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onChange={(_, v) => onAssigneeChange(v)}
          options={assignees}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assignee"
              name="assignee"
            />
          )}
          value={selectedAssignee}
        />
      </Cell>
      <Cell>
        <TextField
          fullWidth
          disabled={searchValue !== ''}
          key="sort-by-select"
          label="Sort By"
          name="sort"
          onChange={(e) => onOrderSortChange('sort', e.target.value)}
          select
          value={sortBy}
        >
          {sortByOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>
      </Cell>
    </Box>

    <Box sx={{ ...fiveColumnRowSx, mb: 0 }}>
      <TripSearchDateRangeFilters
        departureDateFrom={departureDateFrom}
        departureDateTo={departureDateTo}
        matchDateFrom={matchDateFrom}
        matchDateTo={matchDateTo}
        pickerLabels={{
          matchFrom: 'Request Start',
          matchTo: 'Request End',
        }}
        variant="barePickers"
        onChange={onDateFilterChange}
      />
      <Cell>
        <TextField
          fullWidth
          select
          disabled={searchValue !== ''}
          key="order-by-select"
          label="Order By"
          name="order"
          onChange={(e) => onOrderSortChange('order', e.target.value)}
          value={orderBy}
        >
          {orderByOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>
      </Cell>
    </Box>

    <Stack direction={{ mobile: 'column', desktop: 'row' }} spacing={2}>
      <ChipSelectList
        chipOptions={statusChips}
        label="Status"
        selectedChips={selectedStatusChips}
        onChange={onStatusChipChange}
      />
    </Stack>
  </Box>
);
export default AllFilters;
